/**
 * BaseComparison — a two-column comparison component.
 *
 * Variants:
 *  "default"          — both sides on dark neutral bg; right side gets a primary/5 overlay.
 *  "transparent-left" — left side has no background; right side gets primary/10 bg.
 *
 * ComparisonListItem (same file):
 *  variant "positive" — blue check icon, white text
 *  variant "negative" — red X icon, muted text
 *
 * Props per side (ComparisonSide):
 *  label       — small uppercase label above the headline             (optional)
 *  labelColor  — "primary" | "secondary"                             (default: "primary")
 *  headline    — the section headline                                 (required)
 *  tag         — h1..h6 for the headline                             (default: "h3")
 *  items       — array of { text, variant? }                         (required)
 *  cta         — optional { label, url } call-to-action link
 */

import { Check, X } from "lucide-react";
import BaseLabel from "./BaseLabel";
import BaseHeadline from "./BaseHeadline";
import BaseGhostButton from "./BaseGhostButton";

// ─── List Item ────────────────────────────────────────────────────────────────

type ListItemVariant = "positive" | "negative";

type ComparisonListItemProps = {
  children: React.ReactNode;
  variant?: ListItemVariant;
};

export function ComparisonListItem({ children, variant = "positive" }: ComparisonListItemProps) {
  const isPositive = variant === "positive";
  return (
    <div className="flex gap-4 items-start">
      <div className="mt-1 flex-shrink-0">
        {isPositive ? (
          <Check className="w-5 h-5 text-primary" strokeWidth={2.5} />
        ) : (
          <X className="w-4 h-4 text-error" strokeWidth={2.5} />
        )}
      </div>
      <p className={`font-text text-base leading-relaxed ${isPositive ? "text-base-content" : "text-base-content/70"}`}>
        {children}
      </p>
    </div>
  );
}

// ─── Types ────────────────────────────────────────────────────────────────────

type ComparisonItem = {
  text: string;
  variant?: ListItemVariant;
};

type ComparisonSide = {
  label?: string;
  labelColor?: "primary" | "secondary";
  headline: string;
  tag?: "h1" | "h2" | "h3" | "h4" | "h5" | "h6";
  items: ComparisonItem[];
  cta?: { label: string; url: string };
};

type BaseComparisonProps = {
  left: ComparisonSide;
  right: ComparisonSide;
  variant?: "default" | "transparent-left";
  classProps?: string;
};

// ─── Internal panel ───────────────────────────────────────────────────────────

function ComparisonPanel({
  side,
  bgClass,
  overlay,
}: {
  side: ComparisonSide;
  bgClass: string;
  overlay?: string;
}) {
  return (
    <div className={`relative ${bgClass} p-10 sm:p-16`}>
      {overlay && <div className={`absolute inset-0 ${overlay} pointer-events-none`} />}
      <div className="relative z-10 flex flex-col gap-10">
        <div>
          {side.label && (
            <BaseLabel label={side.label} color={side.labelColor ?? "primary"} classProps="mb-4 block" />
          )}
          <BaseHeadline headline={side.headline} tag={side.tag ?? "h3"} classProps="my-0" headlineClassProps="pb-0" />
        </div>

        <div className="flex flex-col gap-6">
          {side.items.map((item, i) => (
            <ComparisonListItem key={i} variant={item.variant ?? "positive"}>
              {item.text}
            </ComparisonListItem>
          ))}
        </div>

        {side.cta && (
          
          <BaseGhostButton text={side.cta.label} url={side.cta.url}  />
        )}
      </div>
    </div>
  );
}

// ─── Main export ──────────────────────────────────────────────────────────────

export function BaseComparison({ left, right, variant = "default", classProps }: BaseComparisonProps) {
  const isDefault = variant === "default";

  const leftBg       = isDefault ? "bg-neutral" : "";
  const rightBg      = isDefault ? "bg-neutral" : "bg-primary/5 rounded-2xl border-base-300/30 border";
  const rightOverlay = isDefault ? "bg-primary/5" : undefined;

  const outerBorder = isDefault
    ? "border border-base-300/30 rounded-2xl overflow-hidden"
    : "";

  return (
    <div className={`w-full ${outerBorder} ${classProps ?? ""}`}>
      <div className="grid md:grid-cols-2 gap-px ">
        <ComparisonPanel side={left}  bgClass={leftBg} />
        <ComparisonPanel side={right} bgClass={rightBg} overlay={rightOverlay} />
      </div>
    </div>
  );
}
